import { useEffect, useState } from "react";
import { type ComponentConfig } from "@puckeditor/core";
import {
  getWindowJobsiteConfig,
  type JobsiteBusinessContext,
} from "../../types/runtime-config";
import { FormBlock, type FormBlockProps } from "./form-block";
import { FormBlockFields } from "./form-block.fields";

/**
 * The Form-block picker: the business's placeable forms (contact + user
 * forms), fetched from the public list. Runs in the dashboard's fields panel,
 * which exposes the editing site via window.__SERVICEPRO_CONFIG__ (the same
 * source page-settings-fields uses).
 */
function FormKeyField({
  value,
  onChange,
}: {
  value?: string;
  onChange: (v: string) => void;
}) {
  const [options, setOptions] = useState<{ key: string; name: string }[] | null>(null);

  useEffect(() => {
    const config = getWindowJobsiteConfig();
    const apiUrl = config?.apiUrl ?? "";
    if (!config?.siteId) {
      setOptions([]);
      return;
    }
    let alive = true;
    fetch(`${apiUrl}/api/sites/${config.siteId}/forms`)
      .then((res) => (res.ok ? (res.json() as Promise<{ forms: { key: string; name: string }[] }>) : null))
      .then((body) => {
        if (alive) setOptions(body?.forms ?? []);
      })
      .catch(() => {
        if (alive) setOptions([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <select
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: "100%", padding: "8px", borderRadius: 6, border: "1px solid #e2e8f0" }}
    >
      <option value="">{options === null ? "Loading forms…" : "Choose a form…"}</option>
      {(options ?? []).map((o) => (
        <option key={o.key} value={o.key}>
          {o.name}
        </option>
      ))}
    </select>
  );
}

// The builder-independent props Puck manages; resolved business context is
// injected by render() below.
type FormBlockPuckProps = Omit<FormBlockProps, "businessSlug" | "hasTrackingPhone" | "editorHint">;

export const FormBlockConfig: ComponentConfig<FormBlockPuckProps> = {
  label: "Form",
  fields: {
    formKey: {
      ...FormBlockFields.formKey,
      type: "custom",
      render: ({ value, onChange }) => (
        <FormKeyField value={value as string | undefined} onChange={onChange} />
      ),
    },
    heading: { ...FormBlockFields.heading, contentEditable: true },
    subheading: { ...FormBlockFields.subheading, contentEditable: true },
    submitButtonText: FormBlockFields.submitButtonText,
    successMessage: FormBlockFields.successMessage,
    anchorId: FormBlockFields.anchorId,
    backgroundColor: FormBlockFields.backgroundColor,
  },
  defaultProps: {
    formKey: "contact",
    heading: "",
    subheading: "",
    submitButtonText: "",
    successMessage: "Thank you! We'll be in touch soon.",
    backgroundColor: "",
  },
  render: ({ id, puck, ...props }) => {
    const puckMetadata = puck?.metadata as { business?: JobsiteBusinessContext } | undefined;
    const windowConfig = getWindowJobsiteConfig();
    const business = puckMetadata?.business || windowConfig?.business;

    return (
      <FormBlock
        id={id}
        {...props}
        businessSlug={business?.slug}
        hasTrackingPhone={!!(business as JobsiteBusinessContext | undefined)?.hasTrackingPhone}
        editorHint={!!windowConfig?.isEditing}
      />
    );
  },
};
